import { ApolloClient, InMemoryCache, ApolloLink, NetworkStatus } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { makeLogout } from 'src/utils/auth';
import { getAccessToken } from 'src/utils/tokenService';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { captureException } from '@sentry/react';

import { API_DEBUG, isDev } from 'src/config';

const uploadLink = createUploadLink({ uri: `${process.env.BASE_URL}api/public/api/graphql` });

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      captureException(new Error(`GraphQL Error: ${message}`), {
        tags: {
          context: 'graphql_error',
        },
        extra: {
          path: JSON.stringify(path, null, 2),
          locations: JSON.stringify(locations, null, 2),
          extensions: JSON.stringify(extensions, null, 2),
        },
      });

      if (message === 'Unauthenticated.') {
        makeLogout({ noEvents: true });
      }
    });
  }
  if (networkError) {
    captureException(networkError, {
      tags: {
        context: 'network_error',
      },
      extra: {
        networkError: JSON.stringify(networkError, null, 2),
      },
    });
  }
});

const authLink = setContext(async (operation, { headers }) => {
  const token = await getAccessToken();

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : undefined,
    },
  };
});

// --- DEBUG: Log all GQL operations ---
let gqlRequestCount = 0;
const loggingLink = new ApolloLink((operation, forward) => {
  gqlRequestCount++;
  const num = gqlRequestCount;
  const operationType =
    operation.query.definitions[0]?.kind === 'OperationDefinition'
      ? (operation.query.definitions[0] as any).operation
      : 'unknown';
  console.log(`[GQL #${num}] ${operationType.toUpperCase()} ${operation.operationName}`, operation.variables);
  const startTime = performance.now();
  return forward(operation).map((result) => {
    const duration = Math.round(performance.now() - startTime);
    console.log(`[GQL #${num}] ${operation.operationName} completed in ${duration}ms`);
    return result;
  });
});
// --- END DEBUG ---

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([...(isDev && API_DEBUG ? [loggingLink] : []), authLink, errorLink, uploadLink]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          scenario(existing, { args, toReference }) {
            return existing || toReference({ __typename: 'Scenario', id: args?.id });
          },
          control(existing, { args, toReference }) {
            return existing || toReference({ __typename: 'Control', id: args?.id });
          },
          incident(existing, { args, toReference }) {
            return existing || toReference({ __typename: 'Incident', id: args?.id });
          },
        },
      },
      OrgUnitIdentity: { merge: true },
      OrgUnitActions: { merge: true },
      WorkspaceUserActions: { merge: true },
      OrganisationUserActions: { merge: true },
      ScenarioRisk: { merge: true },
      AssessmentRisk: { merge: true },
      Meta: { merge: true },
      Owner: { merge: true },
    },
  }),

  defaultOptions: {
    watchQuery: {
      errorPolicy: 'none',
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    },
    query: {
      errorPolicy: 'none',
      fetchPolicy: 'cache-first',
    },
  },
});

type IsQueryLoading = (options: { loading: boolean; data: any; networkStatus: NetworkStatus }) => boolean;

export const isQueryLoading: IsQueryLoading = ({ loading, data, networkStatus }) => {
  return (loading && !data) || networkStatus === NetworkStatus.refetch;
};
